import React from 'react';

import { Badge, Group, Paper, StackProps, Text, Tooltip, createStyles } from '@mantine/core';
import { IconCreditCard, IconEdit, IconLock, IconLockOpen, IconTrashX } from '@tabler/icons-react';
import Menu, { MenuItemProps } from 'components/Menu';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import { AthleteRetrieve } from 'types';
import { Country, isValidCountry } from 'types/enums';

const useStyles = createStyles(theme => ({
  header: {
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'min-content auto min-content min-content',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
}));

interface ICreditCardPaymentMethodCardProps extends StackProps {
  name: string;
  expirationDate: string;
  zip?: string;
  isDefault: boolean;
  paymentMethodId: string;
  athlete?: AthleteRetrieve;
  openEditPaymentMethodForm: (paymentMethodId: string) => void;
  openPaymentMethodDeleteModal?: (paymentMethodId: string) => void;
  country?: Country;
  permission?: boolean;
  isLocked?: boolean;
  failureMessage?: string | null;
  handleUnlock: (paymentMethodId: string) => void;
}

const CountryZipText = ({ country }: { country: Country }): JSX.Element => {
  const { copy } = useLocalization(country);
  return (
    <Text fs='italic' c='dimmed' fz='xs' w='85px' align='right'>
      {copy.address.zipLabel}
    </Text>
  );
};

const CreditCardPaymentMethodCard = ({
  name,
  expirationDate,
  isDefault,
  zip,
  athlete,
  paymentMethodId,
  openEditPaymentMethodForm,
  openPaymentMethodDeleteModal,
  country,
  handleUnlock,
  permission = true,
  isLocked = false,
  failureMessage = null,
}: ICreditCardPaymentMethodCardProps): JSX.Element => {
  const { session } = useAuth();
  const { classes, theme } = useStyles();

  const getMenuItems = (): MenuItemProps[] => {
    const actions: MenuItemProps[] = [
      {
        title: 'Edit',
        icon: <IconEdit size={theme.fontSizes.sm} />,
        onClick: () => openEditPaymentMethodForm(paymentMethodId),
      },
    ];
    if (isLocked) {
      actions.push({
        title: 'Unlock Actions',
        icon: <IconLockOpen size={theme.fontSizes.sm} />,
        onClick: () => handleUnlock(paymentMethodId),
        disabled: !permission,
      });
    }
    if (session?.staff) {
      actions.push({
        title: 'Remove',
        icon: <IconTrashX size={theme.fontSizes.sm} />,
        color: 'red',
        onClick: () => openPaymentMethodDeleteModal?.(paymentMethodId),
      });
    }
    return actions;
  };

  return (
    <Paper shadow='sm' radius='lg' p='md' withBorder bg={isLocked ? 'red.1' : 'white'}>
      <div className={classes.header}>
        {isLocked ? (
          <Tooltip label={failureMessage} disabled={!failureMessage}>
            <IconLock color={theme.colors.red[6]} />
          </Tooltip>
        ) : (
          <IconCreditCard />
        )}
        <Text truncate>{name}</Text>
        {isDefault ? (
          <Badge size='sm' variant='outline' color='kiloBlue'>
            Default
          </Badge>
        ) : (
          <div />
        )}
        <Menu
          position='bottom-end'
          items={getMenuItems()}
          disabled={!permission || !!athlete?.primaryAccount}
          color={isLocked ? 'red.8' : 'kiloBlue'}
        />
      </div>
      <Group spacing='lg'>
        <Text fs='italic' c='dimmed' fz='xs' w='85px' align='right'>
          Expiration Date
        </Text>
        <Text fz='sm'>{expirationDate}</Text>
      </Group>
      {isValidCountry(country) && (
        <Group spacing='lg'>
          <CountryZipText country={country} />
          <Text fz='sm'>{zip}</Text>
        </Group>
      )}
    </Paper>
  );
};

export default CreditCardPaymentMethodCard;
